import React from 'react';
import PhotoCaptureModalEnhanced from './PhotoCaptureModalEnhanced';
import { enhancedPhotoProps } from '../utils/photoModalProps';
import type { PhotoResult } from '../types/photo';

// Re-exported from the one shared definition (src/types/photo.ts) so the files importing
// { PhotoResult } from this modal keep working unchanged.
export type { PhotoResult };

/**
 * "Upload de foto" — now a door into the one photo editor, not an editor of its own.
 *
 * This file used to carry a second, older editor: its own cropper, its own zoom/rotation sliders
 * and a "Fundo" swatch. `PhotoCaptureModalEnhanced` carried the newer one, with tabs, the
 * area-selection tool and face detection. Which editor an operator got depended on which screen
 * they opened the photo from — the same vehicle photo looked and behaved differently depending on
 * the route in, and a fix to one never reached the other.
 *
 * The props are deliberately unchanged, so none of the ~9 call sites move. See
 * utils/photoModalProps for the translation between the two shapes.
 */
interface PhotoUploadModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: PhotoResult) => void;
  title?: string;
  cropShape?: 'round' | 'rect';
  aspectRatio?: number;
  /** Dark chrome; the editor defaults to the app's light theme. */
  darkMode?: boolean;
  /** Base64 image to open on — a photo being re-cropped rather than replaced. */
  initialImage?: string;
  /** No face to find: a vehicle, a pet, a document. */
  disableFaceDetection?: boolean;
  /** Enables the IP-camera tab, which needs a customer to list cameras for. */
  customerId?: string;
}

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Upload de Foto',
  cropShape = 'rect',
  aspectRatio,
  initialImage,
  disableFaceDetection = false,
  customerId,
  darkMode = false,
}) => (
  <PhotoCaptureModalEnhanced
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    darkMode={darkMode}
    {...enhancedPhotoProps('upload', {
      title,
      cropShape,
      aspectRatio,
      initialImage,
      disableFaceDetection,
      customerId,
    })}
  />
);

export default PhotoUploadModal;
